import React, { createContext, useContext, useState, useEffect } from "react";

// Local imports of files to serve as backup fallbacks (the "restore" mechanism)
import localPanFront from "../Assets/Logos/5.png";
import localPanBack from "../Assets/Logos/6.png";
import localVoter from "../Assets/UntitledDesign.png";
import localRcFront from "../Assets/Logos/Front.png";
import localRcBack from "../Assets/Logos/Back.png";
import localAshok from "../Assets/Logos/ashok.png";
import localAshok1 from "../Assets/Logos/ashok1.png";
import localAadhaarLogo from "../Assets/Logos/Aadhaar.png";
import localVoteLogo from "../Assets/Logos/vote.png";
import localPhoneIcon from "../Assets/Logos/old-typical-phone.png";

const AssetsContext = createContext(null);

const CLOUDINARY_URLS = {
  panFront: "https://res.cloudinary.com/adea6udt/image/upload/q_auto,f_auto/v1783494728/tgeoi9xksl4prftjepf4.png",
  panBack: "https://res.cloudinary.com/adea6udt/image/upload/q_auto,f_auto/v1783494732/hfp34l5skejp5ms4jalj.png",
  voter: "https://res.cloudinary.com/adea6udt/image/upload/q_auto,f_auto/v1783494738/x3ma3uc1s8jhhgkhymrf.png",
  rcFront: "https://res.cloudinary.com/adea6udt/image/upload/q_auto,f_auto/v1783494739/grxfmkd92z27mpstcqv0.png",
  rcBack: "https://res.cloudinary.com/adea6udt/image/upload/q_auto,f_auto/v1783494739/q1bvxdshcohkgz9tbxql.png",
  ashok: "https://res.cloudinary.com/adea6udt/image/upload/q_auto,f_auto/v1783494877/ga4rduq9wlf7t9bpzgus.png",
  ashok1: "https://res.cloudinary.com/adea6udt/image/upload/q_auto,f_auto/v1783494880/fupay0358gj2zzkyboc0.png",
  aadharLogo: "https://res.cloudinary.com/adea6udt/image/upload/q_auto,f_auto/v1783494881/xjskmivmk9w4bvxgbk60.png",
  voteLogo: "https://res.cloudinary.com/adea6udt/image/upload/q_auto,f_auto/v1783494882/agng1xiic8qby5tp8foo.png",
  phoneIcon: "https://res.cloudinary.com/adea6udt/image/upload/q_auto,f_auto/v1783494883/hxgvb1ryuorgjnkavbiz.png",
};

const FALLBACKS = {
  panFront: localPanFront,
  panBack: localPanBack,
  voter: localVoter,
  rcFront: localRcFront,
  rcBack: localRcBack,
  ashok: localAshok,
  ashok1: localAshok1,
  aadharLogo: localAadhaarLogo,
  voteLogo: localVoteLogo,
  phoneIcon: localPhoneIcon,
};

// Helper: Convert URL response blob to base64
const getBase64Image = async (url) => {
  const res = await fetch(url, { mode: "cors" });
  if (!res.ok) throw new Error(`HTTP error ${res.status}`);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const getCachedAsset = (key, url) => {
  try {
    const cached = localStorage.getItem(`veecho_asset_${key}`);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.url === url) {
        return parsed.base64;
      }
    }
  } catch (e) {
    console.warn("[AssetsContext] Error reading from cache:", e);
  }
  return null;
};

const cacheAsset = (key, url, base64) => {
  try {
    localStorage.setItem(`veecho_asset_${key}`, JSON.stringify({ url, base64 }));
  } catch (e) {
    console.warn("[AssetsContext] Error writing to cache:", e);
  }
};

export const AssetsProvider = ({ children }) => {
  const [assetsReady, setAssetsReady] = useState(false);
  const [assets, setAssets] = useState(FALLBACKS);

  useEffect(() => {
    const loadAssets = async () => {
      // First, quickly load whatever is cached in localStorage to make UI responsive
      const loaded = { ...FALLBACKS };
      let allCached = true;

      for (const [key, url] of Object.entries(CLOUDINARY_URLS)) {
        if (url) {
          const cached = getCachedAsset(key, url);
          if (cached) {
            loaded[key] = cached;
          } else {
            allCached = false;
          }
        }
      }

      if (allCached) {
        setAssets(loaded);
        setAssetsReady(true);
      }

      // Perform full verification and fetch any missing or outdated templates in background
      const updated = { ...loaded };
      const fetchPromises = Object.entries(CLOUDINARY_URLS).map(async ([key, url]) => {
        if (!url) return;
        try {
          const cached = getCachedAsset(key, url);
          if (cached) return; // already loaded from cache

          const base64 = await getBase64Image(url);
          updated[key] = base64;
          cacheAsset(key, url, base64);
        } catch (err) {
          console.error(`[AssetsContext] Preloading failed for ${key}, falling back to local asset:`, err);
        }
      });

      await Promise.all(fetchPromises);
      setAssets(updated);
      setAssetsReady(true);
    };

    loadAssets();
  }, []);

  return (
    <AssetsContext.Provider value={{ assetsReady, assets }}>
      {children}
    </AssetsContext.Provider>
  );
};

export const useAssets = () => {
  const context = useContext(AssetsContext);
  if (!context) {
    throw new Error("useAssets must be used within an AssetsProvider");
  }
  return context;
};
